/* @module csi/_DndHTML5Mixin */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom-class",
	"dojo/on",
	"dojo/promise/all",
	"dijit/Destroyable",
	"csi/_base/Deferred",
	"csi/_base/underscore",
	"csi/dndHTML5",
	"csi/html",
	"csi/image/_base",
	"csi/locale",
	"csi/message",
	"csi/service/fileService",
	"csi/page/localeProvider",
], function(declare, lang, domClass, on, all, Destroyable, Deferred, _, dndHTML5, html, csiImage, locale, message, fileService, localeProvider) {

	/**
	 * This is HTML5 Drag and Drop mixin module. To be used with widgets. Each widget may connect drop
	 * event to one or several domNodes. Every time you connect this to domNode you may define one or two callbacks:
	 *  - callback for onDrop - this is required
	 *  - callback for onDropError - optional. If not defined, this module will show default error message dialog
	 *
	 * How to use:
	 *
	 * Add this module to your widget declaration:
	 * declare([Widget, _DndHTML5Mixin] , {...
	 *
	 * Connect drop area(s)' domNode(s) and callbacks using function dndHTML5Init():
	 *
	 *  dndHTML5Init(domNode, callback, callbackErr, bBodyPaste, owner, bPopup)
	 *
	 *  You need to call this function  several times to connect to several nodes.
	 *  Example: csi/heading/refImage
	 *
	 * Another way to initialize DnD is "Toggling Initialization". This is useful if you
	 *  plan to toggle DnD On/Off during your widget life circle. In this keys mixin must be
	 *  mixed in to you widget (widget is destroyable owner). this._dndHTML5Handlers will be
	 *  used to hold handlers and ONLY SINGLE DROP ZONE per widget is possible.
	 *
	 *  dndHTML5Toggle: function(bActive, domNode, callback, callbackErr, bBodyPaste){
	 *  Example: csi/property/widget, csi/image/Browser, csi/widget/SvgCanvasEditor
	 *
	 *  Callback function declaration;
	 *
	 *  Image Drag Drop handler
	 *  @param {File[]} files - list of dropped files
	 *  @param {DomNode} domNode
	 *  yourFunctionName: function(files, event){
	 *  	this = files[0];
	 *  	this.processingFunction(files);
	 *  },
	 *
	 *  Example: ImageReplaceSelect._onDropImage: function(self, files){...
	 *
	 *  CallbackErr function declaration
	 *
	 *  Image Drag Drop Error handler
	 *  @param {String} msg - error message
	 *  yourFunctionName: function(msg){
	 *  	...process error, show message, whatever....
	 *  },
	 *
	 * This mixin will prevent pointer operations for all drop node children by adding "pointer-events: none"
	 *
	 * Use CSS .csi-drop-msg to distinguish "drop here" message. Color will change when dragging over.
	 * Example of these classes usage: ImageReplaceSelect module template - ImageReplaceSelect.html
	 */

	const CSS_DROPZONE = "csi-drop-zone";
	const CSS_DROPZONE_DIALOG = "csi-dialog-drop-zone";
	const CSS_PASTE_FOCUS = html.CSS_PASTE_FOCUS;

	const ERR_VALIDATION_IMAGE = "Action_ErrorImage";
	const ERR_VALIDATION_FILE = "Action_ErrorFile";


	/**
	 *  If "owner" does not have function own() then just add listener events
	 *  @param {Ddestroyable | any } owner
	 *  @param {DomNode} domNode
	 *  @param {string} eventName
	 *  @param {function} callback
	 */
	function ownOn(owner, domNode, eventName, callback) {
		if (owner.own) {
			owner.own(on(domNode, eventName, callback));
		} else {
			on(domNode, eventName, callback);
		}
	}
	/**
	 * Check if a string is a base64 encoded image
	 * @param {string} imageString
	 * @return {string}
	 */
	function dndHTML5IsBase64ImageString(imageString) {
		return imageString.indexOf("data:image") === 0;
	}
	/**
	 * Return filename from type string
	 * String will be of format "image/type"
	 * @param {string} filetype
	 * @return {string}
	 */
	function getFileNameExtension(filetype){
		return filetype.split("/")
		.pop()
		.toLowerCase();
	}
	

	/**
	 * Create a new name for a file of given type
	 * String will be of format "image/type"
	 *  @param {string} imageString
	 * @return {string}
	 */
	function dndHTML5CreateFileNameFromType(imageString) {
		return "newimage." + getFileNameExtension(imageString);
	}

	/**
	 * Get file name extension from the data type on string,
	 * Takes string of format "data:image/type"
	 * @param {string} type
	 * @return {string}
	 */
	function dndHTML5GetMIMETypeFromTypeString(type) {
		return type.substring(5);
	}

	return declare(null, {

		/** @property {Destroyable} _dndHTML5Handlers - object holds HTML5 dnd handlers for toggling variant of initialization */
		_dndHTML5Handlers: null,

		/**
		 * HTML5 Drag and Drop initialization
		 * @param {DomNode} node - drop zone node
		 * @param {Function} callback
		 * @param {Function} [callbackErr] - this argument can be null or omitted to use standard error message
		 * @param {boolean} [bBodyPaste=false] - if true, catch paste event from the entire body. Useful for modal dialogs with single landing zone
		 *     do not use this on non-dialog pages, as it will conflict with ImageBrowser, which use bBodyPaste=true
		 * @param {Destroyable} [owner=this]
		 * @param {Context} [bPopup] - this should be supplied if mixin is not used on widget (example: mixin used on refImage)
		 * @param {boolean} [bNoPasteAnchor] - if true, pasteAnchor will not be created
		 * @param {boolean} [bSkipImageValidation] - if true, suppress Image type validation on UI, only use it for non images. However, Server will still validate it and reject unwanted types
		 */
		dndHTML5Init: function(domNode, callback, callbackErr, bBodyPaste, owner, bPopup, bNoPasteAnchor, bSkipImageValidation) {
			var cssClass = (bPopup || this.bPopup) ? CSS_DROPZONE_DIALOG : CSS_DROPZONE;

			// Do not allow to re-initialize domNode that already initialized, except
			// if owner is defined, which means that the caller handle re-initialization itself
			// Example: Image Browser does this to use bBodyPaste=true so, it has to disable dnd on hide and re-initilize on show.
			if (!owner && domClass.contains(domNode, cssClass)) {
				return;
			}
			if (owner) {
				owner._dndHTML5DialogLevel = bBodyPaste ? dndHTML5.getDialogLevel() : -1;
			}

			owner = owner || this;
			ownOn(owner, domNode, "drop", lang.hitch(this, this._dndHTML5Drop, domNode, callback, callbackErr, bSkipImageValidation));
			ownOn(owner, (bBodyPaste ? document.body : domNode), "paste", lang.hitch(this, this._dndHTML5Paste, domNode, callback, callbackErr, owner, bSkipImageValidation));
			ownOn(owner, domNode, "dragover", lang.hitch(this, this._dndHTML5DragOver, domNode));
			ownOn(owner, domNode, "dragenter", lang.hitch(this, this._dndHTML5DragEnter, domNode, owner, bNoPasteAnchor));
			ownOn(owner, domNode, "dragleave", lang.hitch(this, this._dndHTML5DragLeave, domNode));
			ownOn(owner, domNode, "mouseenter", lang.hitch(this, this._dndHTML5MouseEnter, domNode, owner, bNoPasteAnchor));

			domClass.add(domNode, cssClass);
		},

		/**
		 * Activate / Deactivate HTML5 dnd event handling.
		 * Use this initialization if you plan to toggle DnD on/off during lifecircle of your widget
		 * It is advised that the module use ether this functon or direct call to dndHTML5Init to avoid errors with owners
		 * Used for disabling paste capturing when showing browser dialog
		 * @param {boolean} bActive
		 * @param {DomNode} node - drop zone node
		 * @param {Function} onDropImage - callback
		 * @param {boolean} [bBodyPaste=false] - if true, catch paste event from the entire body. Useful for modal dialogs with single landing zone
		 * @param {boolean} [bNoPasteAnchor] - if true, pasteAnchor will not be created
		 * @param {boolean} [bSkipImageValidation] - if true, suppress Image type validation on UI, nly use it for non images. However, Server will still validate it and reject unwanted types
		 */
		dndHTML5Toggle: function(bActive, domNode, onDropImage, onDropImageError, bBodyPaste, bNoPasteAnchor, bSkipImageValidation) {
			if (bActive && !this._dndHTML5Handlers) {
				this._dndHTML5Handlers = new declare([Destroyable])();
				this.dndHTML5Init(domNode, onDropImage, onDropImageError, bBodyPaste, this._dndHTML5Handlers, false, bNoPasteAnchor, bSkipImageValidation);
			} else if (!bActive && this._dndHTML5Handlers) {
				if (domNode) {
					this.dndHTML5Deinit(domNode);
				}
				this._dndHTML5Handlers.destroy();
				delete this._dndHTML5Handlers;
			}
		},

		/**
		 * Remove drop zone CSS class so domNode will not react on drag-over events.
		 * Note: This will not remove event listeners - they should be removed by widget by destroying the owner
		 * Example: SvgCanvasEditor - widget toggles editor's enabled/disable state.
		 * 		On enable widget calls dndHTML5Init and specify owner for listeners
		 * 		On disable widget removes listeners by destroying the owner and calls this function.
		 * @param {DomNode} node - drop zone node
		 */
		dndHTML5Deinit: function(domNode) {
			// Remove drop zone CSS
			domClass.remove(domNode, CSS_DROPZONE_DIALOG);
			domClass.remove(domNode, CSS_DROPZONE);
			// Remove Paste anchor
			var jPasteNode = $(domNode).find("." + CSS_PASTE_FOCUS);
			if (jPasteNode.length) {
				html.destroyElement(jPasteNode[0]);
			}
		},

		/**
		 * onMouseEnter event handler. Create paste anchor if required.
		 * When image get replaced, domNode being recreated and existing anchor deleted, so we re-create it next time user move mouse over the picture.
		 * @param {DomNode} domNode
		 * @param {Destroyable} owner
		 * @param {boolean} bNoPasteAnchor - if true, pasteAnchor will not be created
		 * @param {DragEvent} event
		 */
		_dndHTML5MouseEnter: function(domNode, owner, bNoPasteAnchor, event) {
			if (!bNoPasteAnchor) {
				this._dndHTML5CreatePasteAnchor(domNode, owner);
			}
		},

		/**
		 * onPaste event handler
		 * If drop zone is not visible or not top most dialog, or if pasted data is not a valid image file type,
		 * then return without stopping event to allow other elements process this paste event
		 * Paste image and stop event
		 * @param {DomNode} domNode
		 * @param {Function} callback
		 * @param {Function} callbackErr
		 * @param {DragEvent} event
		 */
		_dndHTML5Paste: function(domNode, callback, callbackErr, owner, bSkipImageValidation, event) {
			// Check if element is hidden
			if (!html.isShown(domNode)) return;

			// If paste targeted to text control - return, let text control process it
			var textControlTypes = ["text", "textarea", "password"];
			var bTextControl = event.target.type && (textControlTypes.indexOf(event.target.type.toLowerCase()) >= 0);
			if (bTextControl) return;

			// Check if there are no files in dataTransfer
			if (!this._dndHTML5ValidatePasted(event.clipboardData.files)) return;

			// Check if top most dialog
			var lvl = owner ? owner._dndHTML5DialogLevel : -1;
			if ((lvl >= 0) && (lvl < dndHTML5.getDialogLevel())) return;

			// Proceed with event
			this._dndHTML5Drop(domNode, callback, callbackErr, bSkipImageValidation, event);
			dndHTML5.stopEvent(event);
		},

		/**
		 * ondrop event handler
		 * Turn off drop zone highlight and call ondrop callback
		 * @param {DomNode} domNode
		 * @param {Function} callback
		 * @param {Function} callbackErr
		 * @param {boolean} bSkipImageValidation
		 * @param {DragEvent} event
		 */
		_dndHTML5Drop: function(domNode, callback, callbackErr, bSkipImageValidation, event) {
			this.dndHTML5SetDragOver(domNode, false);
			if (dndHTML5.isOwnDrag()) {
				return;
			}
			const dataTransfer = event.dataTransfer || event.clipboardData; // Make compatible with "paste", structures are the same
			const imgsSrc = this._dndHTML5GetImgSrcList(dataTransfer);
			let files = Array.from(dataTransfer.files || []);
			if (files.length || imgsSrc.length) {
				if(!files.length){
					const filtered = imgsSrc.filter(dndHTML5IsBase64ImageString);
					files = filtered.map((element) => {
						const type = dndHTML5GetMIMETypeFromTypeString(this.dndHTML5GetType(element));
						return html.convertBase64toFile(element, dndHTML5CreateFileNameFromType(type), type);
					});
				}

				all(bSkipImageValidation || this._dndHTML5ValidateImageFiles(files)).then(
					() => {
						// _dndHTML5DownloadFiles only attempts download if there are no files found on the datatransfer items, and no img source that are converted
						// In the case where it does attempt to download, it throws and error due to C8 CORS policy, and suggests user use the copy paste instead.
						this._dndHTML5DownloadFiles(files, imgsSrc).then(
							() => {
								if (files.length) {
									this._dndHTML5ValidateFiles(files).then(() => {
										callback.call(this, files, domNode);
									}, (err) => {
										const msg = (err instanceof fileService.FileExtError) ? ERR_VALIDATION_FILE : ERR_VALIDATION_IMAGE;
										this._dndHTML5ErrorDialogFileType(msg);
									})
								} else {
									this._dndHTML5ErrorDialogFileType(ERR_VALIDATION_FILE);
								}
							}, (err) => {
								var errMsg = locale.resource("ErrorDownloadingFile");
								if (callbackErr) {
									callbackErr.call(this, errMsg);
								} else {
									message.errorDialog({message: errMsg});
								}
							}
						)
					},
					(err) => {
						const msg = err.message || err;
						const message = locale.resource(msg);
						this._dndHTML5ErrorDialogFileType(message);
					}
				)
			}
		},

		/**
		 * ondragover event handler
		 * Set dropEffect to "copy" mode, allowing drop
		 * @param {DomNode} domNode
		 * @param {DragEvent} event
		 */
		_dndHTML5DragOver: function(domNode, event) {
			dndHTML5.stopEvent(event);

			var dataTransfer = event.dataTransfer;
			// Do not allow files from our own page (aka "Prevent accidental drag-drops")
			dataTransfer.dropEffect = dndHTML5.isOwnDrag() ? 'none' : 'copy';
		},

		/**
		 * ondragenter event handler
		 * Turn on drop zone highlight
		 * @param {DomNode} domNode
		 * @param {Destroyable} owner
		 * @param {boolean} bNoPasteAnchor - if true, pasteAnchor will not be created
		 * @param {DragEvent} event
		 */
		_dndHTML5DragEnter: function(domNode, owner, bNoPasteAnchor, event) {
			if (!dndHTML5.isOwnDrag() && (domNode == event.target)) {
				this.dndHTML5SetDragOver(domNode, true);
				if (!bNoPasteAnchor) {
					this._dndHTML5CreatePasteAnchor(domNode, owner);
				}
			}
		},

		/**
		 * ondragleave event handler
		 * Turn off drop zone highlight
		 * @param {DomNode} domNode
		 * @param {DragEvent} event
		 */
		_dndHTML5DragLeave: function(domNode, event) {
			if (domNode == event.target) {
				this.dndHTML5SetDragOver(domNode, false);
			}
		},

		/**
		 * Create Paste Anchor if required.
		 * "Paste Anchor" is little icon (material icon "center_focus_strong") which indicates that given domNode will receive paste event and process pasted content.
		 * Each drop zone has own instance of Paste Anchor. It's visibility being changed via CSS "opacity" attribute which is 0 by default, and change on :focus and :hover.
		 * Many Paste Anchors can exists simultaneously. But only one Paste Anchor might be visible at the same time.
		 * Note: When image get replaced, domNode being recreated and existing anchor deleted, so we re-create it next time user move mouse over the picture.
		 * @param {DomNode} domNode
		 * @param {Destroyable} owner
		 */
		_dndHTML5CreatePasteAnchor: function(domNode, owner) {
			if (!$(domNode).find("." + CSS_PASTE_FOCUS).length) {
				var pasteNode = html.create("span", {
					"class": "material-icons " + CSS_PASTE_FOCUS,
					innerText: "center_focus_strong",
					title: locale.resource("ImagePasteHere"),
					tabIndex: 0
				}, domNode, "first");
				// Stop event to prevent full screen image preview
				ownOn(owner, pasteNode, "click", dndHTML5.stopEvent);
			}
		},

		/**
		 * Parse dataTransfer in attempt to extract list of img.src
		 * @param {DataTransfer} dataTransfer - HTML5 Drag and Drop data structure
		 * @return {string[]} - list of URLs
		 */
		_dndHTML5GetImgSrcList: function(dataTransfer) {
			var imgsSrc = [];
			try { // required for IE which raises exception if try to get data of nonexistent type
				var data = dataTransfer.getData("text/html");
				var _imgSrc = $(data).attr("src");
				if (_imgSrc) {
					imgsSrc.push(_imgSrc);
				} else {
					_.each($(data).find("img"), function(img) {
						imgsSrc.push(img.src);
					});

				}
			} catch (ex) { };

			try {
				if (!imgsSrc.length) {
					data = dataTransfer.getData("text/uri-list").trim() || dataTransfer.getData("text/x-moz-url").trim();
					if (data) {
						imgsSrc.push(data);
					}
				}
			} catch (ex) { }


			try {
				if (!imgsSrc.length) {
					data = dataTransfer.getData("text").trim();
					if (data) {
						imgsSrc.push(data);
					}
				}
			} catch (ex) { }

			return imgsSrc;
		},

		/**
		 * Display "Invalid File Type" error message
		 */
		_dndHTML5ErrorDialogFileType: function(messageResource) {
			const msg = locale.resource(messageResource);
			console.error(msg);
			message.errorDialog({message: msg});
		},

		/**
		 * Validate file name
		 * @param {File[]} files
		 * @return {Deferred[]} - return array of promises
		 */
		_dndHTML5ValidateImageFiles: function(files) {
			const dfds = [];
			files.forEach((file) => {
				const fileName = file.name;
				if (fileService.isZipFileName(fileName)) {
					dfds.push(fileService.validateZipIsTurntable(file, false, localeProvider));
				} else {
					const dfd = new Deferred()
					csiImage.isValidFormat(fileName) ? dfd.resolve(fileName) : dfd.reject(ERR_VALIDATION_IMAGE);
					dfds.push(dfd);
				}
			});
			return dfds;
		},

		/**
		 * Validate if pasted files are valid for paste:
		 *  - there ae files
		 *  - there are more then
		 * Pasted data i
		 */
		_dndHTML5ValidatePasted: function(files) {
			if (!files || (files.length != 1)) {
				return false;
			}
			var file = files[0];
			return file.type && this._dndHTML5ValidateMimeType(file.type);
		},

		/**
		 * Validate mime type
		 * @param {string} fType - should be "image/gif" or "image/png" or "image/{any valid image file type}"
		 * @return {boolean} - return "true" for valid image type
		 */
		_dndHTML5ValidateMimeType: function(fType) {
			if (fType.indexOf("image")) { // type must start with word "image"
				return false;
			}
			return csiImage.isValidFormat(fType.replace("/", "."));
		},

		/**
		 * Check if files with image extensions are really images or not
		 * @param {File[]|Files} files
		 * return {Deferred}
		 */
		_dndHTML5ValidateFiles: function(files) {
			var dfds = [];
			_.each(files, (file) => {
				dfds.push(fileService.validateFile(file))
			});
			return all(dfds);
		},

		/**
		 * Download files
		 * @param {Blob[]} files - reference to array that will be filled with files data
		 * @param {string[]} imgsSrc - array of URLs
		 * @return {Deferred} - All of deferred
		 */
		_dndHTML5DownloadFiles: function(files, urls) {
			var dfds = [];
			var self = this;
			if (!files.length && urls.length) {
				_.each(urls, function(url) {
					var dfd = new Deferred();
					var request = new XMLHttpRequest();

					request.open('GET', url, true);
					request.responseType = 'blob';
					request.onload = function() {
						dfd.resolve(request);
					};
					request.onerror = function(err) {
						dfd.reject(this.statusText);
					};
					dfd.then(function(request) {
						try {
							var fType = self.dndHTML5GetType(request.response.type);
							var fName = self.dndHTML5FileNameFromUrl(url) || fileService.getFileNameFromMimeType(fType);
							if (self._dndHTML5ValidateMimeType(fType)) {
								files.push(new File([request.response], fName, {type: fType}));
							}
						} catch (ex) {
							files.push(request.response); // IE still don't support operator "new" for File
						}
					});
					request.send();
					dfds.push(dfd);
				});
			}
			return all(dfds);
		},

		/**
		 * Set "csi-drag-hover" class on domNode
		 * @param {DomNode} domNode
		 * @param {bActive} boolean
		 */
		dndHTML5SetDragOver: function(domNode, bActive) {
			domClass.toggle(domNode, "csi-drag-over", bActive);
		},

		/**
		 * Extract file name from URL
		 * @param {string} url
		 * @return {string}
		 */
		dndHTML5FileNameFromUrl: function(url) {
			// url contain encoded image
			if (url.indexOf("data:") == 0) {
				return "";
			}
			var filenameRegEx = /[<>:"\/\\|?*\x00-\x1F]/g;
			var fileName = url.substring(url.lastIndexOf('/') + 1) || "";
			var validFileName = !filenameRegEx.test(fileName);
			return validFileName ? fileName : "";
		},

		
		/** 
		 * Extract type from provided string
		 * IE is adding charset to the type, so that is list of values separated with ";"
		 * IE Example: "image/jpeg; charset=UTF-8", other browser just have: "image/jpeg"
		 * @param {string} type
		 * @return {string}
		 */
		dndHTML5GetType: function(type) {
			var n = type.indexOf(';');
			return (n >= 0) ? type.substring(0, n) : type;
		},

		/**
		 * Release _dndHTML5Handlers handlers
		 */
		dndHTML5Destroy: function() {
			if (this._dndHTML5Handlers) {
				this._dndHTML5Handlers.destroy();
			}
		}
	});
});